import { NgTemplateOutlet, AsyncPipe } from '@angular/common';
import { Component, Input, OnDestroy, OnInit, inject } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import {
  NgbNavChangeEvent,
  NgbNav,
  NgbNavItem,
  NgbNavItemRole,
  NgbNavLinkButton,
  NgbNavLinkBase,
  NgbTooltip,
  NgbNavContent,
  NgbNavOutlet,
} from '@ng-bootstrap/ng-bootstrap';
import { Actions, ofType } from '@ngrx/effects';
import { Memoize } from 'lodash-decorators';
import { Observable, Subscription } from 'rxjs';
import { distinctUntilKeyChanged, first, tap } from 'rxjs/operators';

import { signalGeneratorEnded } from '@app/models/status-resolve';
import { DeviceService } from '@app/services/device.service';
import { MonitoringService } from '@app/services/monitoring.service';
import { SignalGeneratorParameterService } from '@app/services/signal-generator-parameter.service';
import { SignalGeneratorService } from '@app/services/signal-generator.service';
import { TuningControllerService } from '@app/services/tuning-controller.service';
import { ParameterFormComponent } from '@app/shared/parameter-form/parameter-form.component';
import { ParameterFormControlInputGroupAppendComponent } from '@app/shared/parameter-form-control-input-group-append/parameter-form-control-input-group-append.component';
import { startSignalGeneratorStatus, stopSignalGeneratorStatus } from '@app/store/device.actions';
import { Device } from '@app/store/device.model';
import { EncoderConfiguration } from '@app/store/encoder-configuration.model';
import { mapParametersToFieldNameValues, Parameter } from '@app/store/parameter.model';
import { SensorConfiguration } from '@app/store/sensor-configuration.model';
import {
  createSetSignalGeneratorParametersRequestMessage,
  SignalGenerator,
  signalGeneratorMemoizeResolver,
  SignalGeneratorType,
} from '@app/store/signal-generator.model';
import { TuningController } from '@app/store/tuning-controller.model';
import { getCurrentValue } from '@app/util';

@Component({
  selector: 'app-signal-generator',
  templateUrl: './signal-generator.component.html',
  imports: [
    NgbNav,
    NgbNavItem,
    NgbNavItemRole,
    NgbNavLinkButton,
    NgbNavLinkBase,
    NgbTooltip,
    NgTemplateOutlet,
    NgbNavContent,
    FormsModule,
    ReactiveFormsModule,
    ParameterFormControlInputGroupAppendComponent,
    ParameterFormComponent,
    NgbNavOutlet,
    AsyncPipe,
  ],
})
export class SignalGeneratorComponent implements OnInit, OnDestroy {
  private actions$ = inject(Actions);
  private deviceService = inject(DeviceService);
  private monitoringService = inject(MonitoringService);
  private signalGeneratorService = inject(SignalGeneratorService);
  private signalGeneratorParameterService = inject(SignalGeneratorParameterService);
  private tuningControllerService = inject(TuningControllerService);

  device$: Observable<Device>;
  signalGenerators$: Observable<SignalGenerator[]>;

  selectedSignalGeneratorType$ = this.signalGeneratorService.selectedSignalGeneratorType$;
  selectedSignalGenerator$: Observable<SignalGenerator>;
  encoderConfiguration$: Observable<SensorConfiguration | EncoderConfiguration>;
  feedConstantEnabled$: Observable<boolean>;
  feedConstantUnit$: Observable<string>;
  positionActualParameter$: Observable<Parameter>;

  switchedOn$: Observable<boolean>;
  operationEnabled$: Observable<boolean>;
  quickStopActive$: Observable<boolean>;

  isMotionPrevented$: Observable<boolean>;
  motionPreventedMessage$: Observable<string>;

  positionsMonitoringSubscription = new Subscription();
  signalGeneratorSubscription = new Subscription();
  operationEnabledSubscription = new Subscription();

  positionFormControl = new UntypedFormControl('');

  starting = false;

  private _tuningController: TuningController;

  @Input()
  set tuningController(tuningController: TuningController) {
    if (tuningController.id !== this.tuningController?.id) {
      this.device$ = this.deviceService.selectDeviceByDeviceSerialNumber(tuningController.deviceSerialNumber);
      const device = getCurrentValue(this.device$);

      this.signalGenerators$ = this.signalGeneratorService.selectSignalGeneratorsByTuningControllerId(
        tuningController.id,
      );

      this.encoderConfiguration$ = this.deviceService.selectDeviceEncoderConfigurationUsedForPosition(device);
      this.selectedSignalGenerator$ =
        this.tuningControllerService.selectTuningControllerSignalGeneratorBySelectedType(tuningController);

      this.switchedOn$ = this.deviceService.selectDeviceSwitchedOn(device);
      this.operationEnabled$ = this.deviceService.selectDeviceOperationEnabled(device);
      this.quickStopActive$ = this.deviceService.selectDeviceQuickStopActive(device);

      this.operationEnabledSubscription.unsubscribe();
      this.operationEnabledSubscription = this.deviceService
        .selectDeviceOperationEnabled(device)
        .subscribe((operationEnabled) => {
          if (operationEnabled) {
            this.starting = false;
          }
        });

      if (tuningController.type === 'position') {
        this.resubscribeToPositionMonitoring(device);
      }

      this.updatePositionTuningControllerAmplitudeValues(tuningController);

      if (tuningController.signalGeneratorType) {
        this.signalGeneratorService.selectSignalGeneratorType(tuningController.signalGeneratorType);
      }
    }

    this._tuningController = tuningController;
  }

  get tuningController() {
    return this._tuningController;
  }

  get values() {
    const signalGenerator = getCurrentValue(this.selectedSignalGenerator$);
    const signalGeneratorParameters = getCurrentValue(this.signalGeneratorParameters(signalGenerator));

    const values = mapParametersToFieldNameValues(signalGeneratorParameters);

    values.targetInputValue = Number.isFinite(values.target) ? values.target : values.amplitude;

    return values;
  }

  ngOnInit(): void {
    const device = getCurrentValue(this.device$);
    this.positionActualParameter$ = this.deviceService.selectDeviceParameter(device, 'positionActualValue').pipe(
      distinctUntilKeyChanged('unit'),
      tap(() => {
        this.resubscribeToPositionMonitoring(device);
      }),
    );
    this.feedConstantEnabled$ = this.deviceService.selectDeviceFeedConstantEnabled(device);
    this.feedConstantUnit$ = this.deviceService.selectDeviceFeedConstantUnit(device);

    this.isMotionPrevented$ = this.deviceService.selectIsMotionPrevented(device);
    this.motionPreventedMessage$ = this.deviceService.selectMotionPreventedMessage(device);
  }

  ngOnDestroy() {
    this.signalGeneratorSubscription.unsubscribe();
    this.positionsMonitoringSubscription.unsubscribe();
  }

  resubscribeToPositionMonitoring(device: Device) {
    // It is not guaranteed that ngOnDestroy will run before ngOnInit when navigating between the controller types. Without a unique
    // topic unsubscribe stops the monitoring and the position control value would typically be blank.
    this.positionsMonitoringSubscription.unsubscribe();
    this.positionsMonitoringSubscription = this.monitoringService
      .startMonitoringActualPositions(device.serialNumber, 64000, true)
      .subscribe(([primary]: [number, number]) => this.positionFormControl.setValue(primary));
  }

  @Memoize(signalGeneratorMemoizeResolver)
  signalGeneratorParameters(signalGenerator: SignalGenerator) {
    return this.signalGeneratorParameterService.selectSignalGeneratorParametersBySignalGeneratorId(signalGenerator.id);
  }

  onNavChange($event: NgbNavChangeEvent) {
    const type = $event.nextId as SignalGeneratorType;
    this.signalGeneratorService.selectSignalGeneratorType(type);
  }

  onFormSubmit(formGroup: UntypedFormGroup) {
    if (this.starting) {
      return;
    }

    const operationEnabled = getCurrentValue(this.operationEnabled$);
    if (operationEnabled) {
      this.onStopSignalGenerator();
      return;
    }

    const quickStopActive = getCurrentValue(this.quickStopActive$);
    if (quickStopActive) {
      this.onDisableMotionController();
      return;
    }

    this.starting = true;

    const parsedValues = this.signalGeneratorParameterService.parseSignalGeneratorParameterControlValues(
      formGroup.value,
    );
    formGroup.markAsPristine(); // this will allow the parameter form to update the controls to parsed values (hack)
    this.signalGeneratorParameterService.updateSignalGeneratorParameterValues(parsedValues);

    const signalGenerator = getCurrentValue(this.selectedSignalGenerator$);

    const device = getCurrentValue(this.device$);

    const values = this.values;

    const setSignalGeneratorParameters = createSetSignalGeneratorParametersRequestMessage(
      device,
      this.tuningController.type,
      signalGenerator.type,
      values,
    );

    this.deviceService.requestSetSignalGeneratorParameters(setSignalGeneratorParameters);
    this.deviceService.dispatchBeforeStartSignalGenerator(device, signalGenerator.id, values);
    this.deviceService.requestStartSignalGenerator(device, signalGenerator.id, values);

    this.signalGeneratorSubscription.unsubscribe();
    this.signalGeneratorSubscription = this.actions$
      .pipe(
        ofType(startSignalGeneratorStatus, stopSignalGeneratorStatus),
        first((action) => signalGeneratorEnded(action.signalGenerator)),
      )
      .subscribe(() => {
        this.starting = false;
      });
  }

  onPositionUnitChange(unit: string) {
    const device = getCurrentValue(this.device$);
    const positionActualParameter = getCurrentValue(this.positionActualParameter$);
    this.deviceService.updateDeviceParameter(positionActualParameter, { unit });
    this.resubscribeToPositionMonitoring(device);
  }

  onAmplitudeUnitChange(values: any) {
    this.signalGeneratorParameterService.updateSignalGeneratorParameterUnit(values);
  }

  onStopSignalGenerator() {
    const device = getCurrentValue(this.device$);
    this.deviceService.requestStopSignalGenerator(device);
  }

  onDisableMotionController() {
    const device = getCurrentValue(this.device$);
    this.deviceService.requestDisableMotionController(device);
  }

  updatePositionTuningControllerAmplitudeValues(tuningController: TuningController) {
    if (tuningController.type === 'position') {
      const ec = getCurrentValue(this.encoderConfiguration$);
      const amplitude = ec.isHALL ? ec.resolution : Math.floor(ec.resolution / 6); // full rotation for HALL, 60 degrees otherwise

      const signalGenerators = getCurrentValue(this.signalGenerators$);

      signalGenerators.forEach((signalGenerator) => {
        const parameters = getCurrentValue(this.signalGeneratorParameters(signalGenerator));
        parameters.forEach((parameter) => {
          if (parameter.fieldName === 'target' && parameter.value === 0) {
            let value = amplitude;
            if (signalGenerator.type === 'stepResponse' || signalGenerator.type === 'advancedStepResponse') {
              value = Math.floor(ec.resolution) / 180; // 2 degrees for simple and advanced step responses
            }
            this.signalGeneratorParameterService.updateSignalGeneratorParameter(parameter, {
              floatValue: value,
              intValue: value,
              value,
            });
          }
        });
      });
    }
  }

  onSelectSignalGeneratorType(signalGeneratorType: SignalGeneratorType) {
    this.tuningController.signalGeneratorType = signalGeneratorType;
    this.tuningControllerService.updateTuningController({ signalGeneratorType });
  }
}
